// src/pages/stream-management-grid/components/StreamCard.tsx
	import React, { useEffect, useRef, useState } from 'react';
	import Icon from '../../../components/AppIcon';
	import { Button, Tooltip } from '../../../components/AntComponents';
	import ViewDevice, { DeviceData } from '../../stream-management-grid/components/ViewDevice';
	import { mediamtxService } from '@/mediaservices/mediamtxService';
	import { mediasoupService } from '@/mediaservices/mediasoupService';

	/**
	 * Optional: extend the incoming stream type with server state
	 * so we can initialize UI from truth (if you pass it down).
	 */
	type MuteState = {
	  selfMuted?: boolean;
	  adminMuted?: boolean;
	  effectivelyMuted?: boolean;
	};

	type StreamCardProps = {
	  stream: {
		id: string | number;
		deviceId?: number;
		deviceName: string;
		device_name?: string;
		location?: string;
		deviceType?: 'stb' | 'phone'| string;
		status?: 'online' | 'ready' | 'connecting' | 'offline' | string;

		/** MEDIA PATH A — WHEP (MediaMTX) */
		whepUrl?: string | null;

		/** MEDIA PATH B — Mediasoup (when you add your mediasoup player) */
		mediaStream?: MediaStream | null;

		/** Optional server-fed state for optimistic icon init */
		muteState?: MuteState;                // provided in stream_status_update
		videoOff?: boolean;                   // if your backend sends it; else we keep local UI only
	  };

	  /**
	   * Parent callback to actually perform admin actions via WebSocket.
	   * Implement in parent using your mediamtxService:
	   *   - 'mute'  -> mediamtxService.toggleMic(streamId)
	   *   - 'video' -> mediamtxService.toggleVideo(streamId)
	   *   - 'make-host' / 'make-cohost' -> setHost / setCoHost
	   */
	  onStreamAction: (id: string | number, action: string) => void;
	  onDeviceReload?: (deviceSerial: string) => void;
	};

	const StreamCard: React.FC<StreamCardProps> = ({ 
	  stream, 
	  onStreamAction,
	  onDeviceReload
	}) => {
	  // ---------- Local UI state ----------
	  const [isHovered, setIsHovered] = useState(false);
	  const [drawerOpen, setDrawerOpen] = useState(false);
	  const [drawerData, setDrawerData] = useState<DeviceData | null>(null);
	  const videoRef = useRef<HTMLVideoElement | null>(null);
	  const containerRef = useRef<HTMLDivElement | null>(null);
	  /** LOCAL SPEAKER ONLY (allows autoplay when true) */
	  const [localMuted, setLocalMuted] = useState(false);
	  /** NEW: Local mute for STB devices - browser-side only*/
	  const [localStbMuted, setLocalStbMuted] = useState(true);
	  const [remoteMicOff, setRemoteMicOff] = useState<boolean>(
		stream.muteState?.adminMuted ?? false
	  );
	  const [remoteVideoOff, setRemoteVideoOff] = useState<boolean>(false);
	  const [deviceSelfVideoPaused, setDeviceSelfVideoPaused] = useState<boolean>(false);
	  const [deviceSelfMuted, setDeviceSelfMuted] = useState<boolean>(false);
	  const [connectionState, setConnectionState] = useState<string>('new');
	  const [reconnectTrigger, setReconnectTrigger] = useState(0);
	  const [deviceWasInPublishers, setDeviceWasInPublishers] = useState(false);
	  const [isFullscreen, setIsFullscreen] = useState(false);
	  const [isConnecting, setIsConnecting] = useState(false); // New state for buffering

	  const [localStbVideoPaused, setLocalStbVideoPaused] = useState(false);

	  /** If env var is set, drawer won't pass mediaStream (pure MediaMTX mode) */
	  const USE_MEDIAMTX = String(import.meta.env.VITE_USE_MEDIAMTX).toLowerCase() === 'true';
  
	// Check if this is an STB device
	const isStbDevice = stream.deviceType?.toLowerCase() === 'stb';
	const isVideoEffectivelyPaused = isStbDevice ? localStbVideoPaused : (remoteVideoOff || deviceSelfVideoPaused);
	const handleDeviceReload = () => {
		if (onDeviceReload && stream.deviceName) {
			onDeviceReload(stream.deviceName);			// This ensures we attempt to reconnect even if we don't get an ack
				setTimeout(() => {
				setReconnectTrigger(prev => prev + 1);
				}, 1500); // 1.5 second delay to allow server processing
			}
		};

	  const handleConnectionStateChange = (pc: RTCPeerConnection) => {
    const state = pc.connectionState;
    setConnectionState(state);
    if (state === 'connected') {
      setIsConnecting(false);
    }

    // If connection fails/disconnects, trigger a reconnect after a delay
    if (state === 'failed' || state === 'disconnected') {
      console.log(`[StreamCard ${stream.deviceName}] Connection lost, will attempt reconnect`);
      setIsConnecting(false);
      setTimeout(() => {
        if (stream.whepUrl) { // Only if we still have a WHEP URL
          console.log(`[StreamCard ${stream.deviceName}] Triggering reconnect`);
          setReconnectTrigger(prev => prev + 1);
        }
      }, 2000); // Wait 2 seconds before reconnecting
    }
  };

	  useEffect(() => {
		if (stream.muteState) {
		  setRemoteMicOff(stream.muteState.adminMuted ?? false);
		  setDeviceSelfMuted(stream.muteState.selfMuted ?? false);
		}
		
		if (stream.videoOff !== undefined) {
		  setRemoteVideoOff(stream.videoOff);
		}

		// ✅ NEW: Check if stream has videoPauseState data and initialize accordingly
		if (USE_MEDIAMTX && stream.id) {
		  // Get the current video pause state from mediamtxService
		  const streamSummaries = mediamtxService.getStreamSummaries();
		  const currentStream = streamSummaries.find(s => 
			s.id === String(stream.deviceName) || s.id === String(stream.id)
		  );
		  
		  if (currentStream?.meta?.videoPauseState) {
			const videoPauseState = currentStream.meta.videoPauseState;
			setRemoteVideoOff(videoPauseState.adminPaused ?? false);
			setDeviceSelfVideoPaused(videoPauseState.selfPaused ?? false);
		  }
		}
	  }, []);

	  // Handle local video pause/play for STB devices
		useEffect(() => {
			if (!isStbDevice) return;
			
			const v = videoRef.current;
			if (!v) return;
			
			if (localStbVideoPaused) {
				v.pause();
			} else {
				v.play().catch(() => { /* ignore autoplay errors */ });
			}
		}, [localStbVideoPaused, isStbDevice]);

	  useEffect(() => {
		if (!USE_MEDIAMTX || !stream) return;

		const onVideoPauseStateUpdate = (data: { id: string; videoPauseState: any }) => {
		  if (data.id === String(stream.deviceName) || data.id === String(stream.id)) {
			setRemoteVideoOff(data.videoPauseState?.adminPaused ?? false);
			setDeviceSelfVideoPaused(data.videoPauseState?.selfPaused ?? false);
		  }
		};

		mediamtxService.on('videoPauseState', onVideoPauseStateUpdate);
		return () => mediamtxService.off('videoPauseState', onVideoPauseStateUpdate);
	  }, [stream?.deviceName, stream?.id]);


	  // ✅ LISTEN: for mute state updates from WebSocket
	useEffect(() => {
		if (!USE_MEDIAMTX || !stream) return;

		const onMuteUpdate = (data: { id: string; muteState: any }) => {
			if (data.id === String(stream.deviceName) || data.id === String(stream.id)) {
				setRemoteMicOff(data.muteState?.adminMuted ?? false);
				setDeviceSelfMuted(data.muteState?.selfMuted ?? false);
			}
		};

		mediamtxService.on('mute', onMuteUpdate);
		return () => mediamtxService.off('mute', onMuteUpdate);
	}, [stream?.deviceName, stream?.id]);


	  // Keep UI in sync if parent rehydrates props from WS later
	  useEffect(() => {
		setRemoteMicOff(stream.muteState?.adminMuted ?? false); // ✅ Use adminMuted instead of effectivelyMuted
	  }, [stream.muteState?.adminMuted]); // ✅ Watch adminMuted specifically

	  useEffect(() => {
		if (typeof stream.videoOff === 'boolean') setRemoteVideoOff(stream.videoOff);
	  }, [stream.videoOff]);

	  // ============================================================
	  // MEDIA PATH B — MEDIASOUP (when you implement mediasoup player)
	  // ============================================================
	  // If you are receiving a MediaStream (from your Mediasoup consumer),
	  // bind it directly to the <video>. This path takes precedence.
	  useEffect(() => {
		const v = videoRef.current;
		if (!v) return;

		// only handle Mediasoup when we actually have a MediaStream
		if (!stream.mediaStream) return;

		// Bind the incoming MediaStream to the video element
		v.autoplay = true;
		v.playsInline = true;
		v.muted = localMuted; // keep true by default to pass autoplay policies
		v.srcObject = stream.mediaStream;
		v.play().catch(() => { /* ignore autoplay promise errors while muted */ });

		// Cleanup: detach only (do not stop tracks; producer owns them)
		return () => {
		  if (videoRef.current && videoRef.current.srcObject === stream.mediaStream) {
			videoRef.current.srcObject = null;
		  }
		};
	  }, [stream.mediaStream, localMuted]);

	  // ======================================



	  // Apply local speaker toggle to the element only (no renegotiation)
	  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    
    // For STB devices, use localStbMuted for the video element muting
    // For other devices, use localMuted (the existing speaker toggle)
    const shouldBeMuted = isStbDevice ? localStbMuted : localMuted;
    
    v.muted = shouldBeMuted;
    if (!shouldBeMuted) {
      v.play().catch(() => { /* user gesture may be required to unmute */ });
    }
  }, [localMuted, localStbMuted, isStbDevice]);

	  // ---------- UI handlers ----------
	  const toggleSpeaker = () => setLocalMuted((m) => !m);

	  // NEW: Toggle local STB muting (browser-side only)
	  const toggleLocalStbMute = () => {
		setLocalStbMuted(prev => !prev);
	  };

	  const goFullscreen = () => {
		const el = containerRef.current as any;
		if (!el) return;
		if (!isFullscreen) {
		  (el.requestFullscreen ||
			el.webkitRequestFullscreen ||
			el.mozRequestFullScreen ||
			el.msRequestFullscreen)?.call(el);
		} else {
		  (document.exitFullscreen ||
			(document as any).webkitExitFullscreen ||
			(document as any).mozCancelFullScreen ||
			(document as any).msExitFullscreen)?.call(document);
		}
	  };

	  const toggleRemoteMic = () => {
		// For STB devices, handle local muting first, then optionally do remote logic
		if (isStbDevice) {
		  toggleLocalStbMute();
		  // Still send WebSocket command for consistency/logging (optional)
		  const nextMuted = !remoteMicOff;
		  if (USE_MEDIAMTX) {
			console.log("MediaMTX: Toggling mic for STB stream", stream.deviceName);
			mediamtxService.setMicMuted(String(stream.deviceName), nextMuted);
		  } else {
			console.log("MediaSoup: Toggling mic for STB stream", stream.id);
			mediasoupService.setMicMuted(String(stream.deviceName), nextMuted);
		  }
		  setRemoteMicOff(nextMuted);
		} else {
		  // For non-STB devices, use existing logic
		  setRemoteMicOff(currentlyMuted => {
			const nextMuted = !currentlyMuted;
			
			if (USE_MEDIAMTX) {
			  console.log("MediaMTX: Toggling mic for stream", stream.deviceName);
			  mediamtxService.setMicMuted(String(stream.deviceName), nextMuted);
			} else {
			  console.log("MediaSoup: Toggling mic for stream", stream.id);
			  mediasoupService.setMicMuted(String(stream.deviceName), nextMuted);
			}
			
			return nextMuted;
		  });
		}
	  };

		const toggleRemoteVideo = () => {
			// For STB devices, handle video pause/play at browser level only
			if (isStbDevice) {
				const nextPaused = !localStbVideoPaused;
				setLocalStbVideoPaused(nextPaused);
				console.log("STB: Toggling local video pause", stream.deviceName, "paused:", nextPaused);
				
				// Immediately pause/play the video element
				if (videoRef.current) {
					if (nextPaused) {
						videoRef.current.pause();
					} else {
						videoRef.current.play().catch(() => { /* ignore autoplay errors */ });
					}
				}
			} else {
				// For non-STB devices, use backend API
				const nextVideoOff = !remoteVideoOff;
				
				if (USE_MEDIAMTX) {
					console.log("MediaMTX: Sending video toggle request for stream", stream.deviceName, "videoPaused:", nextVideoOff);
					mediamtxService.setVideoPaused(String(stream.deviceName), nextVideoOff);
				} else {
					console.log("MediaSoup: Sending video toggle request for stream", stream.id, "videoPaused:", nextVideoOff);
				}
			}
		};

	  const getDeviceIcon = (deviceType?: string) => {
		switch (deviceType) {
		  case 'STB': return 'Video';
		  case 'phone': return 'Smartphone';
		  default: return 'Video';
		}
	  };

	  const getStatusColor = (status?: string) => {
		switch (status) {
		  case 'live': return 'bg-error';
		  case 'ready': return 'bg-success';
		  case 'connecting': return 'bg-warning';
		  case 'offline': return 'bg-muted-foreground';
		  default: return 'bg-muted';
		}
	  };

	  // Drawer open (note: in MediaMTX mode we don't pass mediaStream to drawer)
	  const openDrawer = () => {
		// Prefer the real numeric ID from parent; else try to parse stream.id
		const numericId =
		  typeof stream.deviceId === 'number'
			? stream.deviceId
			: typeof stream.id === 'number'
			  ? stream.id
			  : Number.isFinite(Number(stream.id))
				? Number(stream.id)
				: undefined;

		const data: DeviceData = {
		  deviceId: numericId, // <-- this is what ViewDevice uses to call /devices/{id}
		  participantName: stream.deviceName, //device_serial_number
		  device_name: stream.device_name,  //device_name for display in drawer
		  deviceType: (stream.deviceType as any) || 'desktop',
		  deviceName: stream.deviceName, // device_serial_number
		  location: stream.location,
		  mediaStream: USE_MEDIAMTX ? null : (stream.mediaStream ?? null),
		};

		setDrawerData(data);
		setDrawerOpen(true);
	  };

	  //   Listen for device status changes from WebSocket
	  useEffect(() => {
		if (!USE_MEDIAMTX) return;

		const handleStreamStatusUpdate = (data: any) => {
		  const publishersInfo = data.streamStatus?.publishersInfo || {};
		  const deviceIsCurrentlyInPublishers = !!publishersInfo[stream.deviceName];
		  // Only trigger reconnect if THIS device's presence in publishers changed
		  if (deviceIsCurrentlyInPublishers && !deviceWasInPublishers) {
			// Device just appeared in publishers (started streaming)
			setDeviceWasInPublishers(true);
			setReconnectTrigger(prev => prev + 1);
		  } else if (!deviceIsCurrentlyInPublishers && deviceWasInPublishers) {
			// Device disappeared from publishers (stopped streaming)
			setDeviceWasInPublishers(false);
			setConnectionState('disconnected');
		  }
		};

		mediamtxService.on('streamStatusUpdate', handleStreamStatusUpdate);
		
		return () => {
		  mediamtxService.off('streamStatusUpdate', handleStreamStatusUpdate);
		};
	  }, [stream.deviceName, deviceWasInPublishers]);

	  // Initialize device publisher state on mount
  useEffect(() => {
    if (!USE_MEDIAMTX) return;

    const streamSummaries = mediamtxService.getStreamSummaries();
    const deviceInStreams = streamSummaries.some(s => 
      s.id === stream.deviceName || s.meta?.deviceSerialNumber === stream.deviceName
    );
    
    setDeviceWasInPublishers(deviceInStreams);
  }, []);

	  // WHEP connection effect - handles initial connection and reconnections
  useEffect(() => {
    if (!stream.whepUrl || !videoRef.current) return;

    let pc: RTCPeerConnection | null = null;
    let aborted = false;
    const v = videoRef.current;

    // Set connecting state when starting WHEP connection
    setIsConnecting(true);

    if (v.srcObject) {
      (v.srcObject as MediaStream).getTracks().forEach((t) => t.stop());
      v.srcObject = null;
    }

    v.autoplay = true;
    v.muted = isStbDevice ? localStbMuted : localMuted;
    v.playsInline = true;

    const waitForIceGatheringComplete = (peer: RTCPeerConnection) =>
      new Promise<void>((resolve) => {
        if (peer.iceGatheringState === 'complete') return resolve();
        const onChange = () => {
          if (peer.iceGatheringState === 'complete') {
            peer.removeEventListener('icegatheringstatechange', onChange);
            resolve();
          }
        };
        peer.addEventListener('icegatheringstatechange', onChange);
        setTimeout(() => {
          peer.removeEventListener('icegatheringstatechange', onChange);
          resolve();
        }, 1000);
      });

    const startWhepWithRetry = async (maxAttempts = 40) => {
      let attempt = 0;
      let lastError: any = null;

      while (!aborted && attempt < maxAttempts) {
        attempt += 1;

        try {
          // Fresh PC each attempt
          pc = new RTCPeerConnection();

          //Monitor connection state changes
          pc.onconnectionstatechange = () => handleConnectionStateChange(pc!);

          // Receive-only
          pc.addTransceiver('video', { direction: 'recvonly' });
          pc.addTransceiver('audio', { direction: 'recvonly' });

          // Attach remote stream to <video>
          pc.ontrack = (e) => {
            if (aborted) return;
            if (v.srcObject !== e.streams[0]) {
              v.srcObject = e.streams[0];
              setConnectionState('connected');
              setIsConnecting(false); // Stop showing buffering when video arrives
            }
          };

          // Build SDP offer
          const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true });
          await pc.setLocalDescription(offer);
          
          //   Wait for ICE gathering
          await waitForIceGatheringComplete(pc);

          const localDesc = pc.localDescription;
          if (!localDesc?.sdp) throw new Error('No local SDP');

          // POST to WHEP
          const res = await fetch(stream.whepUrl!, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/sdp',
              'Accept': 'application/sdp, text/plain',
            },
            body: localDesc.sdp,
          });

          if (!res.ok) {
            throw new Error(`WHEP POST failed: ${res.status} ${res.statusText}`);
          }

          const answerSdp = await res.text();
          if (aborted) return;

          await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

          // Try to play
          try { 
            await v.play();
          } catch {}

          // Success - break the retry loop
          return;
          
        } catch (err) {
          lastError = err;
          console.warn(`[StreamCard ${stream.deviceName}] Attempt ${attempt} failed:`, err);
          try { 
            pc?.getReceivers().forEach(r => r.track?.stop?.()); 
            pc?.close(); 
          } catch {}
          pc = null;

          // Exponential backoff
          const delay = 3000;
          await new Promise(r => setTimeout(r, delay));
        }
      }

      console.error(`[StreamCard ${stream.deviceName}] Connection failed after ${maxAttempts} attempts`);
      setConnectionState('failed');
      setIsConnecting(false); // Stop showing buffering on failure
    };

    startWhepWithRetry();

    return () => {
      aborted = true;
      setIsConnecting(false); // Stop showing buffering on cleanup
      try {
        if (pc) {
          pc.getReceivers().forEach((r) => r.track && r.track.stop && r.track.stop());
          pc.close();
        }
      } catch {}
      pc = null;
      if (v && v.srcObject) {
        (v.srcObject as MediaStream).getTracks().forEach((t) => t.stop());
        v.srcObject = null;
      }
    };
  }, [stream.whepUrl, stream.deviceName, reconnectTrigger, isStbDevice, localStbMuted, localMuted]);

	useEffect(() => {
	  const handleFullscreenChange = () => {
		const el = containerRef.current;
		const fsElement =
		  document.fullscreenElement ||
		  (document as any).webkitFullscreenElement ||
		  (document as any).mozFullScreenElement ||
		  (document as any).msFullscreenElement;
		setIsFullscreen(!!(el && fsElement === el));
	  };

	  document.addEventListener('fullscreenchange', handleFullscreenChange);
	  document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
	  document.addEventListener('mozfullscreenchange', handleFullscreenChange);
	  document.addEventListener('MSFullscreenChange', handleFullscreenChange);

	  return () => {
		document.removeEventListener('fullscreenchange', handleFullscreenChange);
		document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
		document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
		document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
	  };
	}, []);



	  return (
		<div
		  className="relative bg-card border border-border rounded-lg overflow-hidden transition-all duration-200 hover:shadow-lg"
		  onMouseEnter={() => setIsHovered(true)}
		  onMouseLeave={() => setIsHovered(false)}
		>
		  {/* View button */}
		  	<div className="absolute top-3 left-3 z-10">
			<Tooltip title="View Device">
				<Button
				variant="ghost"
				size="sm"
				onClick={openDrawer}
				className="text-white bg-black/40 hover:bg-black/60 rounded-md"
				>
				<Icon name="Eye" size={14} />
				</Button>
			</Tooltip>
			</div>

		  {/* Stream Status */}
		  <div className="absolute top-3 right-3 z-10 flex items-center space-x-1">
			<div className={`w-2 h-2 rounded-full ${getStatusColor(stream.status)} ${stream.status === 'live' ? 'status-pulse' : ''}`} />
			<span className="text-xs font-mono text-white bg-black bg-opacity-60 px-1.5 py-0.5 rounded">
			   {(stream.status === 'live' ? 'online' : (stream.status || 'offline')).toUpperCase()}
			</span>
		  </div>

		  {/* Video (Mediasoup MediaStream OR WHEP) */}
		  <div ref={containerRef} className="relative aspect-video bg-muted">
			{(stream.mediaStream || stream.whepUrl) ? (
			  <>
			    {/* Video element */}
			    <video
				  key={`${String(stream.id)}:${stream.mediaStream ? 'ms' : (stream.whepUrl || '')}`}
				  ref={videoRef}
				  className="w-full h-full object-contain"
			    />
			    
			    {/* Buffering/Connecting Overlay */}
            {isConnecting && (
              <div className="absolute inset-0 bg-black bg-opacity-70 flex flex-col items-center justify-center space-y-3">
                <div className="bg-slate-800 bg-opacity-90 rounded-full p-4">
                  <Icon name="Loader2" size={40} className="text-white animate-spin" />
                </div>
                <span className="text-lg font-medium text-white">Connecting...</span>
                <span className="text-xs text-slate-300">Establishing video stream connection</span>
              </div>
            )}
            
            {/* Video Paused Overlay - shows different messages based on pause source */}
            {isVideoEffectivelyPaused && !isConnecting && (
              <div className="absolute inset-0 bg-black bg-opacity-70 flex flex-col items-center justify-center space-y-3">
                <div className="bg-slate-800 bg-opacity-90 rounded-full p-4">
                  <Icon name="Pause" size={40} className="text-white" />
                </div>
                <span className="text-lg font-medium text-white">
                  {isStbDevice && localStbVideoPaused
                    ? 'Video Paused by Operator'
                    : remoteVideoOff 
                    ? 'Video Paused by Operator' 
                    : 'Video Paused by Device'}
                </span>
                <span className="text-xs text-slate-300">
                  {isStbDevice && localStbVideoPaused
                    ? 'Click the video icon to resume playback'
                    : remoteVideoOff 
                    ? 'Stream video has been paused by the operator' 
                    : 'Device has paused their video stream'}
                </span>
              </div>
            )}
			  </>
			) : (
			  <div className="w-full h-full flex items-center justify-center text-sm text-muted-foreground">
				No stream
			  </div>
			)}

			{/* Hover controls remain the same */}
			{isHovered && !isConnecting && (
			  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
				<div className="flex items-center gap-3">
				  <Button
					variant="ghost"
					size="sm"
					onClick={toggleRemoteMic}
					className={`text-white ${
					  (isStbDevice ? localStbMuted : remoteMicOff) ? 'ring-1 ring-red-500/70' : ''
					}`}
					title={
					  isStbDevice 
						? (localStbMuted ? "Unmute Audio (Local)" : "Mute Audio (Local)")
						: (remoteMicOff ? "Unmute Audio" : "Mute Audio")
					}
				  >
				    <Icon 
					name={(isStbDevice ? localStbMuted : remoteMicOff) ? "VolumeX" : "Volume2"} 
					size={16} 
					className={(isStbDevice ? localStbMuted : remoteMicOff) ? "text-red-400" : ""} 
				    />
				  </Button>

				  {/* Video toggle button (camera icon) */}
				  <Button
					variant="ghost"
					size="sm"
					onClick={toggleRemoteVideo}
					className={`text-white ${isVideoEffectivelyPaused ? 'ring-1 ring-red-500/70' : ''}`}
					title={isVideoEffectivelyPaused ? "Resume Video" : "Pause Video"}
				  >
					<Icon 
					  name={isVideoEffectivelyPaused ? "VideoOff" : "Video"} 
					  size={16} 
					  className={isVideoEffectivelyPaused ? "text-red-400" : ""} 
					/>
				  </Button>
				  <Button
					  variant="ghost"
					  size="sm"
					  onClick={handleDeviceReload}
					  className="text-white"
					  title="Refresh stream for this device"
					>
					  <Icon name="RotateCcw" size={16} />
					</Button>
				  {/* Fullscreen */}
				  <Button
					variant="ghost"
					size="sm"
					onClick={goFullscreen}
					className="text-white"
					title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
				  >
					<Icon name={isFullscreen ? "Minimize" : "Maximize"} size={16} />
				  </Button>
				</div>
			  </div>
			)}
		  </div>

		  {/* Info */}
		  <div className="p-4">
			<div className="flex items-start justify-between mb-2">
			  <div className="flex-1 min-w-0">
				<h3 className="text-sm font-medium text-foreground truncate">{stream.device_name}</h3>
				{stream.location && (
				  <p className="text-xs text-muted-foreground truncate">{stream.location}</p>
				)}
			  </div>
			  <div className="flex items-center space-x-2 ml-2">
				<Icon name={getDeviceIcon(stream.deviceType)} size={16} className="text-muted-foreground" />
			  </div>
			</div>
		  </div>

		  {/* Drawer */}
		  <ViewDevice
			open={drawerOpen}
			mode="stream"
			data={drawerData}
			onClose={() => setDrawerOpen(false)}
			onMakeHost={() => onStreamAction(stream.id, 'make-host')}
			onMakeCoHost={() => onStreamAction(stream.id, 'make-cohost')}
		  />
		</div>
	  );
	};

	export default StreamCard;
